/**
 * One-shot localStorage migration from the legacy REACTJS (CRA) site's
 * auth-storage shape to the V2 Zustand-persist shape. Lets a user who is
 * already signed in on the old site land on V2 without re-authenticating.
 *
 * Old site (REACTJS/src/app/redux/actions/AuthActions.js):
 *   localStorage.accessToken   = "<raw JWT>"
 *   localStorage.userData      = JSON.stringify(user)
 *
 * V2 (src/pages/auth/store/useAuth.ts):
 *   localStorage['yk-auth']    = JSON.stringify({ state: { token, user }, version: 0 })
 *
 * Same JWT format, same backend — we just repack the keys. `useAuth.init()`
 * will then `GET auth/verify` on first paint and silently log the user out
 * if the token turns out to be stale.
 *
 * MUST run before `useAuth` is module-evaluated — Zustand's `persist`
 * middleware reads localStorage at store creation time. `main.tsx` imports
 * this module on its first line for that reason.
 *
 * Legacy keys are intentionally LEFT IN PLACE so the user can still bounce
 * back to the old site logged in during the cutover. `useAuth.logout()`
 * handles the inverse cleanup (see same file).
 */
const LEGACY_TOKEN_KEY = 'accessToken';
const LEGACY_USER_KEY = 'userData';
const V2_AUTH_KEY = 'yk-auth';

export function migrateLegacyAuth(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    // Already in V2 shape — already migrated or natively logged into V2.
    if (localStorage.getItem(V2_AUTH_KEY)) return;

    const token = localStorage.getItem(LEGACY_TOKEN_KEY);
    if (!token) return;

    let user: unknown = null;
    const rawUser = localStorage.getItem(LEGACY_USER_KEY);
    if (rawUser) {
      try {
        user = JSON.parse(rawUser);
      } catch {
        // Legacy site occasionally stored a non-JSON string; treat as missing.
      }
    }

    localStorage.setItem(
      V2_AUTH_KEY,
      JSON.stringify({ state: { token, user }, version: 0 }),
    );
  } catch {
    // Private mode / disabled storage / cross-origin iframe — degrade to
    // "not logged in" and let the regular login flow take over.
  }
}

// Side-effect: run the migration the moment this module is evaluated. The
// import order in `main.tsx` is what guarantees this runs BEFORE `useAuth`
// is reached transitively — ES module imports are evaluated in source
// order, and any imports placed after this line haven't started yet.
migrateLegacyAuth();
